// Pose-guided cut-out cleanup (v0.7.1). The person segmenter sometimes keeps pieces of the
// room that look "person-like" (a chair back, a pile of clothes, a held garment). The body
// keypoints tell us where the person actually is: everything outside a generous hull of
// capsules around the head, torso and limbs is removed from the mask, and the segmenter is
// fed a crop around the person (more pixels on the person → cleaner edges).
//
// DOM-free: the camera worker turns this into shader uniforms (live) and applies it on the
// CPU for the full-resolution still; the tests check the geometry. Keypoints are the
// MediaPipe Pose layout (x, y, z, visibility), normalised to the frame.

export interface Capsule { ax: number; ay: number; bx: number; by: number; r: number }

const VIS = 0.45;
/** [a, b, radius in shoulder widths] — torso, arms, legs; hands reach past the wrist */
const SEGMENTS: [number, number, number][] = [
  [11, 12, 0.55], [23, 24, 0.55], [11, 23, 0.6], [12, 24, 0.6], [11, 24, 0.6], [12, 23, 0.6],
  [11, 13, 0.32], [13, 15, 0.3], [15, 19, 0.3], [15, 17, 0.3],
  [12, 14, 0.32], [14, 16, 0.3], [16, 20, 0.3], [16, 18, 0.3],
  [23, 25, 0.38], [25, 27, 0.32], [27, 31, 0.28],
  [24, 26, 0.38], [26, 28, 0.32], [28, 32, 0.28],
];

export const MAX_CAPS = 24;

/**
 * Body hull in pixels of a w×h image, or null when the pose is too weak to trust
 * (then the mask is left alone). `margin` adds a safety band (in shoulder widths), e.g.
 * more while the person moves fast.
 */
export function bodyCapsules(kp: ArrayLike<number> | null, w: number, h: number, margin = 0.35): Capsule[] | null {
  if (!kp || kp.length < 33 * 4) return null;
  const P = (i: number) => ({ x: kp[i * 4] * w, y: kp[i * 4 + 1] * h, v: kp[i * 4 + 3] });
  const ls = P(11), rs = P(12);
  if (ls.v < VIS || rs.v < VIS) return null;
  const sw = Math.hypot(ls.x - rs.x, ls.y - rs.y);
  if (!(sw > 4)) return null;
  const caps: Capsule[] = [];
  for (const [a, b, r] of SEGMENTS) {
    const A = P(a), B = P(b);
    if (A.v < VIS || B.v < VIS) continue;
    caps.push({ ax: A.x, ay: A.y, bx: B.x, by: B.y, r: (r + margin) * sw });
  }
  // head: around the nose / ears, tall enough for hair and a raised hand above it
  const nose = P(0), le = P(7), re = P(8);
  if (nose.v >= VIS) {
    const ear = le.v >= VIS && re.v >= VIS ? Math.hypot(le.x - re.x, le.y - re.y) : sw * 0.45;
    const r = Math.max(ear * 1.15, sw * 0.4) + margin * sw;
    const mid = { x: (ls.x + rs.x) / 2, y: (ls.y + rs.y) / 2 };
    caps.push({ ax: nose.x, ay: nose.y - ear * 0.5, bx: nose.x, by: nose.y - ear * 0.5, r });
    caps.push({ ax: mid.x, ay: mid.y, bx: nose.x, by: nose.y, r: Math.max(ear * 0.9, sw * 0.35) + margin * sw }); // neck
  }
  return caps.slice(0, MAX_CAPS);
}

/** Bounds [x0, y0, x1, y1] (px) of the hull, or null without a trusted pose. */
export function hullBounds(kp: ArrayLike<number> | null, w: number, h: number, margin = 0.3): [number, number, number, number] | null {
  const caps = bodyCapsules(kp, w, h, margin);
  if (!caps) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const c of caps) {
    x0 = Math.min(x0, c.ax - c.r, c.bx - c.r); x1 = Math.max(x1, c.ax + c.r, c.bx + c.r);
    y0 = Math.min(y0, c.ay - c.r, c.by - c.r); y1 = Math.max(y1, c.ay + c.r, c.by + c.r);
  }
  return [Math.max(0, x0), Math.max(0, y0), Math.min(w, x1), Math.min(h, y1)];
}

/** 0..1: how much of the mask to keep at pixel (x, y) (1 inside the hull, soft edge of `feather` px). */
export function gateAt(caps: Capsule[], x: number, y: number, feather: number): number {
  let best = Infinity;
  for (const c of caps) {
    const dx = c.bx - c.ax, dy = c.by - c.ay;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - c.ax) * dx + (y - c.ay) * dy) / l2)) : 0;
    const d = Math.hypot(x - (c.ax + t * dx), y - (c.ay + t * dy)) - c.r;
    if (d < best) best = d;
  }
  if (best <= 0) return 1;
  return best >= feather ? 0 : 1 - best / feather;
}

/**
 * Crop around the person (frame pixels) for the segmenter: keypoint bounds + room for
 * hair, raised hands and the feet; null when the person already fills the frame or the
 * pose is missing (then the whole frame is used).
 */
export function personRoi(kp: ArrayLike<number> | null, w: number, h: number): { x: number; y: number; w: number; h: number } | null {
  const caps = bodyCapsules(kp, w, h, 0.5);
  if (!caps) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const c of caps) {
    x0 = Math.min(x0, c.ax - c.r, c.bx - c.r); x1 = Math.max(x1, c.ax + c.r, c.bx + c.r);
    y0 = Math.min(y0, c.ay - c.r, c.by - c.r); y1 = Math.max(y1, c.ay + c.r, c.by + c.r);
  }
  const padX = (x1 - x0) * 0.12, padTop = (y1 - y0) * 0.15, padBottom = (y1 - y0) * 0.08;
  x0 = Math.max(0, x0 - padX); x1 = Math.min(w, x1 + padX);
  y0 = Math.max(0, y0 - padTop); y1 = Math.min(h, y1 + padBottom);
  const rw = x1 - x0, rh = y1 - y0;
  if (!(rw > 16 && rh > 16)) return null;
  if (rw * rh > 0.75 * w * h) return null;
  return { x: x0, y: y0, w: rw, h: rh };
}
